import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import type { ReactNode } from 'react'
import { supabase, activeOrgId } from '@/lib/supabase'
import { useAuth } from './AuthContext'

interface Organization {
  id: string
  name: string
  slug: string | null
  role: string
  created_at: string
}

interface Location {
  id: string
  name: string
  location_code: string
  airport_code: string | null
  iata_code: string | null
  status: string | null
}

interface TenantContextValue {
  organizations: Organization[]
  currentOrganization: Organization | null
  locations: Location[]
  currentLocation: Location | null
  isLoading: boolean
  setCurrentOrganization: (org: Organization | null) => void
  setCurrentLocation: (location: Location | null) => void
  refreshOrganizations: () => Promise<void>
  refreshLocations: () => Promise<void>
}

const TenantContext = createContext<TenantContextValue | null>(null)

interface TenantProviderProps {
  children: ReactNode
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { user, isAuthenticated } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentOrganization, setCurrentOrganizationState] =
    useState<Organization | null>(null)
  const [locations, setLocations] = useState<Location[]>([])
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [locationsLoading, setLocationsLoading] = useState(false)

  // Wrap setCurrentOrganization to also update the header ref
  const setCurrentOrganization = useCallback(
    (org: Organization | null) => {
      activeOrgId.current = org?.id ?? ''
      setCurrentOrganizationState(org)
    },
    []
  )

  // Manual refresh of organizations (for external use)
  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([])
      return
    }

    try {
      interface OrgRow {
        id: string
        role: string
        name: string
        slug: string | null
        created_at: string
      }

      const { data, error } = await supabase.rpc('get_organizations') as {
        data: OrgRow[] | null
        error: Error | null
      }

      if (error) {
        console.error('Error fetching organizations:', error)
        return
      }

      const orgs: Organization[] = (data || []).map((row) => ({
        id: row.id,
        name: row.name || 'Unnamed Organization',
        slug: row.slug,
        role: row.role,
        created_at: row.created_at,
      }))

      setOrganizations(orgs)
    } catch (error) {
      console.error('Error fetching organizations:', error)
    }
  }, [isAuthenticated])

  // Manual refresh of locations (for external use)
  const refreshLocations = useCallback(async () => {
    if (!currentOrganization) {
      setLocations([])
      return
    }

    try {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, location_code, airport_code, iata_code, status')
        .eq('organization_id', currentOrganization.id)
        .is('deleted_at', null)
        .order('name')

      if (error) {
        console.error('Error fetching locations:', error)
        return
      }

      setLocations(data || [])
    } catch (error) {
      console.error('Error fetching locations:', error)
    }
  }, [currentOrganization])

  const lastUserIdRef = useRef<string | null>(null)
  const initializingRef = useRef(false)

  // Initialize tenant data when user changes
  useEffect(() => {
    let mounted = true

    async function initTenant() {
      if (!mounted) return
      if (initializingRef.current) return
      initializingRef.current = true

      const isSameUser = user?.id && lastUserIdRef.current === user.id
      const shouldShowLoading = !isSameUser

      if (shouldShowLoading) {
        setIsLoading(true)
      }

      let didSetOrg = false

      if (isAuthenticated && user) {
        lastUserIdRef.current = user.id
        try {
          // Fetch last active org preference
          const { data: prefs } = await supabase
            .from('user_preferences')
            .select('last_active_org_id')
            .eq('user_id', user.id)
            .single()

          const lastActiveOrgId = prefs?.last_active_org_id

          interface OrgRow {
            id: string
            role: string
            name: string
            slug: string | null
            created_at: string
          }

          // If we have a last active org, set the header before fetching
          if (lastActiveOrgId) {
            activeOrgId.current = lastActiveOrgId
          }

          const { data, error } = await supabase.rpc('get_organizations') as {
            data: OrgRow[] | null
            error: Error | null
          }

          if (!mounted) return

          if (error) {
            console.error('Error fetching organizations:', error)
          } else {
            const orgs: Organization[] = (data || []).map((row) => ({
              id: row.id,
              name: row.name || 'Unnamed Organization',
              slug: row.slug,
              role: row.role,
              created_at: row.created_at,
            }))

            setOrganizations(orgs)

            if (orgs.length > 0) {
              if (!currentOrganization || !isSameUser) {
                // Prefer last active org, otherwise first org
                const defaultOrg =
                  (lastActiveOrgId && orgs.find((o) => o.id === lastActiveOrgId)) ||
                  orgs[0]
                setCurrentOrganization(defaultOrg)
                setLocationsLoading(true)
                didSetOrg = true
              }
            }
          }
        } catch (error) {
          console.error('Error fetching organizations:', error)
        }
      } else {
        lastUserIdRef.current = null
        if (!mounted) return
        setOrganizations([])
        setCurrentOrganization(null)
        setLocations([])
        setCurrentLocation(null)
      }

      if (mounted) {
        if (shouldShowLoading && !didSetOrg) {
          setIsLoading(false)
        }
      }

      initializingRef.current = false
    }

    initTenant()

    return () => {
      mounted = false
      initializingRef.current = false
    }
  }, [isAuthenticated, user?.id])

  // Fetch locations when organization changes
  useEffect(() => {
    let mounted = true

    async function fetchLocations() {
      if (!currentOrganization) {
        setLocations([])
        setCurrentLocation(null)
        setLocationsLoading(false)
        return
      }

      setLocationsLoading(true)

      try {
        const { data, error } = await supabase
          .from('locations')
          .select('id, name, location_code, airport_code, iata_code, status')
          .eq('organization_id', currentOrganization.id)
          .is('deleted_at', null)
          .order('name')

        if (!mounted) return

        if (error) {
          console.error('Error fetching locations:', error)
          setLocationsLoading(false)
          setIsLoading(false)
          return
        }

        setLocations(data || [])
      } catch (error) {
        console.error('Error fetching locations:', error)
      } finally {
        if (mounted) {
          setLocationsLoading(false)
          setIsLoading(false)
        }
      }
    }

    fetchLocations()

    return () => {
      mounted = false
    }
  }, [currentOrganization])

  // Persist org preference in background when org changes
  useEffect(() => {
    if (!currentOrganization || !user) return

    supabase.rpc('set_active_organization', {
      p_org_id: currentOrganization.id,
    }).then(({ error }) => {
      if (error) console.warn('Failed to persist org preference:', error.message)
    })
  }, [currentOrganization, user])

  const combinedIsLoading = isLoading || locationsLoading

  const value: TenantContextValue = {
    organizations,
    currentOrganization,
    locations,
    currentLocation,
    isLoading: combinedIsLoading,
    setCurrentOrganization,
    setCurrentLocation,
    refreshOrganizations,
    refreshLocations,
  }

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
